import { DOCUMENT } from '@angular/common';
import { Inject, Injectable } from '@angular/core';
import { Data } from '@angular/router';
import { BehaviorSubject, distinctUntilChanged } from 'rxjs';

export type AppLanguage = 'fr' | 'ar';

/**
 * Route data of pages holding a form in progress: they are not re-created when the language changes
 * (their texts still switch through the translate pipe). Usage: `data: KEEP_ON_LANGUAGE_CHANGE`.
 */
export const KEEP_ON_LANGUAGE_CHANGE = { keepOnLanguageChange: true } as const;

export function keepsStateOnLanguageChange(routeData: Data): boolean {
  return routeData['keepOnLanguageChange'] === true;
}

const STORAGE_KEY = 'currentLang';
const DEFAULT_LANGUAGE: AppLanguage = 'fr';

/**
 * Single source of truth for the language of the storefront.
 *
 * It has no dependency on ngx-translate or HttpClient, so it can be injected anywhere — including HTTP
 * interceptors (TranslateService itself depends on HttpClient through its loader).
 * Switching the UI language is done through TranslationService.setLanguage(), which also loads the translations.
 */
@Injectable({ providedIn: 'root' })
export class LanguageService {
  private readonly languageSubject = new BehaviorSubject<AppLanguage>(this.readStoredLanguage());

  /** Emits the current language, then every change. */
  readonly language$ = this.languageSubject.pipe(distinctUntilChanged());

  constructor(@Inject(DOCUMENT) private document: Document) {
    this.applyToDocument(this.current);
  }

  get current(): AppLanguage {
    return this.languageSubject.value;
  }

  get isArabic(): boolean {
    return this.current === 'ar';
  }

  get direction(): 'rtl' | 'ltr' {
    return this.isArabic ? 'rtl' : 'ltr';
  }

  set(language: string): void {
    const next = LanguageService.normalize(language);
    try {
      localStorage.setItem(STORAGE_KEY, next);
    } catch {
      // storage unavailable (private mode): the language still applies for this visit
    }
    this.applyToDocument(next);
    this.languageSubject.next(next);
  }

  static normalize(language: string | null | undefined): AppLanguage {
    return language === 'ar' ? 'ar' : DEFAULT_LANGUAGE;
  }

  private readStoredLanguage(): AppLanguage {
    try {
      return LanguageService.normalize(localStorage.getItem(STORAGE_KEY));
    } catch {
      return DEFAULT_LANGUAGE;
    }
  }

  /** Flips the whole page layout for arabic. */
  private applyToDocument(language: AppLanguage): void {
    this.document.documentElement.lang = language;
    this.document.documentElement.dir = language === 'ar' ? 'rtl' : 'ltr';
  }
}
